// ozg-storage.js
import Formio from 'formiojs/Formio'
import config from '@/config/config.js'

// Basis-API-URL (für Download/Delete & alle anderen API-Calls)
const API_BASE = (config.API_URL && config.API_URL.trim()) || '';
const API = API_BASE.replace(/\/+$/, '') + '/api/';     // <-- hier fest /api/ anhängen
const UPLOAD_BASE = ((config.API_UPLOAD_URL && config.API_UPLOAD_URL.trim()) || API).replace(/\/+$/, '/') ;

const UPLOAD_URL   = UPLOAD_BASE + 'upload';
const DOWNLOAD_URL = API + 'storage/download';
const DELETE_URL   = API + 'storage';

const COMMUNITY_CONTEXT_URL = API + 'communityContext';

function getParam(name) {
  return new URL(window.location.href).searchParams.get(name);
}

let cachedContextToken = null;

async function getCommunityContextToken() {
  if (cachedContextToken) return cachedContextToken;

  const isLocal =
    /(^localhost$)|(^127\.0\.0\.1$)/i.test(window.location.hostname) ||
    window.location.hostname.startsWith('192.168.') ||
    window.location.hostname.endsWith('.local');

  // Lokal: kein CommunityContext nötig -> Dummy setzen
  if (isLocal) {
    cachedContextToken = 'LOCAL_DEV';
    return cachedContextToken;
  }

  const c = getParam('c');
  if (!c) throw new Error('CommunityIdentifier fehlt');

  const res = await fetch(`${COMMUNITY_CONTEXT_URL}?c=${encodeURIComponent(c)}`, {
    method: 'GET',
    credentials: 'include'
  });

  const text = await res.text();
  if (!res.ok) throw new Error(`CommunityContext fehlgeschlagen (${res.status}): ${text}`);
  const json = JSON.parse(text);

  cachedContextToken = json.communityContextToken;
  return cachedContextToken;
}

// NEU: Set für bereits gesehene Datei-Inhalte (Hash)
const seenUploadContentHashes = new Set()

// NEU: SHA-256 Hash eines File-Objekts berechnen (Hex-String)
async function sha256FileHex (file) {
  try {
    if (!file || !file.arrayBuffer || !window.crypto || !window.crypto.subtle) {
      return null // Hashing nicht möglich -> Duplikat-Erkennung deaktiviert
    }
    const buf = await file.arrayBuffer()
    const hashBuf = await window.crypto.subtle.digest('SHA-256', buf)
    const bytes = new Uint8Array(hashBuf)
    let hex = ''
    for (const b of bytes) {
      hex += b.toString(16).padStart(2, '0')
    }
    return hex
  } catch (e) {
    // Debug: zeigt, ob Hashing/crypto auf dem Client nicht geht (z. B. alte Browser/iframe restrictions)
    // console.warn('[OZG] sha256FileHex fehlgeschlagen', e)
    return null
  }
}


// ========== Upload per XHR ==========
// Zweck: Upload über XHR (Progressbar), plus serverseitige Fehler sauber in UI bringen
function xhrPost (url, formData, headers = {}, progressCb) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('POST', url, true)

    Object.entries(headers).forEach(([k, v]) => {
      if (v != null && String(v).length) xhr.setRequestHeader(k, v)
    })

    if (typeof progressCb === 'function' && xhr.upload) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) progressCb(e.loaded / e.total)
      }
    }

    xhr.onreadystatechange = () => {
      if (xhr.readyState !== 4) return

      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText))
        } catch (e) {
          // kein Error-Objekt nach außen geben -> sonst "Error:"
          reject('Technischer Fehler: Serverantwort ist kein gültiges JSON.')
        }
      } else {
        let errPayload = null
        const ct = xhr.getResponseHeader('Content-Type') || ''
        if (ct.includes('application/json')) {
          try { errPayload = JSON.parse(xhr.responseText) } catch {}
        }

        // Wichtig: immer als reiner String rejecten
        if (errPayload && errPayload.messageDe) {
          // Optional: Meta für Debugging, ohne UI zu verschmutzen
          window.__ozgLastUploadError = {
            code: errPayload.code,
            reqId: errPayload.reqId,
            details: errPayload.details || null
          }
          return reject(errPayload.messageDe)
        }

        const fallbackByStatus = {
          400: 'Ungültiger Upload. Bitte Dateityp und Anzahl prüfen.',
          401: 'Nicht angemeldet. Bitte erneut einloggen.',
          413: 'Datei oder Gesamtgröße zu groß.',
          415: 'Ungültiges Upload-Format. Erwartet wird multipart/form-data.',
          429: 'Zu viele Uploads in kurzer Zeit. Bitte später erneut versuchen.',
          500: 'Serverfehler beim Upload. Bitte später erneut versuchen.'
        }
        const msg = fallbackByStatus[xhr.status] || `Upload fehlgeschlagen (${xhr.status}).`
        reject(msg)
      }
    }

    // kein Error-Objekt -> sonst "Error:"
    xhr.onerror = () => reject('Netzwerkfehler beim Upload.')
    xhr.send(formData)
  })
}


// ========== Auto-Patch: File.deleteFile soll immer Provider verwenden ==========
// Zweck: Form.io verwendet manchmal intern nicht deinen Provider beim Löschen.
// Dieser Patch zwingt Delete über fs.deleteFile(...) damit dein Backend sauber löscht.
;(function autoPatchDelete () {
  function apply () {
    const File = window.Formio?.Components?.components?.file
    if (!File?.prototype || File.prototype.__ozgPatched) return !!File?.prototype

    const orig = File.prototype.deleteFile
    File.prototype.deleteFile = function (fi) {
      try {
        const fs = this && this.fileService
        if (fs && typeof fs.deleteFile === 'function') {
          // Debug: zeigt, ob dein Patch greift
          // console.log('[OZG] File.patch: using provider.deleteFile')
          return fs.deleteFile(fi, this.component?.options || {})
        }
      } catch (e) {
        // Debug: Patch-Fehler zur Laufzeit
        // console.error('[OZG] File.patch runtime error', e)
      }
      // Debug: zeigt, ob Formio auf Original zurückfällt (unerwünscht)
      // console.log('[OZG] File.patch: fallback to original deleteFile')
      return orig ? orig.call(this, fi) : Promise.resolve()
    }

    File.prototype.__ozgPatched = true
    // Debug: einmaliger Hinweis, dass Patch aktiv ist
    // console.log('[OZG] File component patched')
    return true
  }

  if (!apply()) {
    let n = 0
    const t = setInterval(() => {
      if (apply() || ++n > 50) clearInterval(t)
    }, 200)
  }
})()

// ========== OZG Storage Provider ==========
class OZGStorageProvider {
  static title = 'OZG Storage'
  static name = 'ozg'

  constructor (options = {}) {
    this.options = options || {}
  }

  // NEU: async, damit wir vor dem Upload den Datei-Hash prüfen können
  async uploadFile (file, fileName, dir, progressCallback, url, options) {

    // Debug: wenn Formio einen anderen Namen übergibt als file.name (komisches Browserverhalten)
    // console.log('fileName param (from Formio):', fileName)
    // console.log('resolvedName (we will send):', (file && file.name) ? file.name : fileName)

    // Debug: schnelle Übersicht der Upload-Parameter (hilft bei "geht nur lokal", "geht nur im Wizard", etc.)
    // console.group('[OZG][UPLOAD] start')
    // console.log('file.name:', file?.name)
    // console.log('file.size:', file?.size)
    // console.log('file.type:', file?.type)
    // console.log('file instanceof File:', file instanceof File)
    // console.log('UPLOAD_URL:', UPLOAD_URL)
    // console.log('options:', options)
    // console.groupEnd()

    // --------- Duplikat-Erkennung nach Dateiinhalt (Frontend) ---------
    const hash = await sha256FileHex(file)
    if (hash) {
      if (seenUploadContentHashes.has(hash)) {
        const msg = 'Hinweis: Es scheint, dass Sie diese Datei bereits hochgeladen haben.'

        // Debug: zeigt, dass Hash-Duplikat greift (wenn Nutzer "mehrfach klickt" oder Widget doppelt submit)
        // console.warn('[OZG] DUPLIKAT erkannt (Hash)', { fileName: file?.name, hash })

        // Event für Widget/Host, falls du z.B. Snackbar/Toast bauen willst
        try {
          if (typeof window !== 'undefined' && window.dispatchEvent) {
            window.dispatchEvent(new CustomEvent('ozg-duplicate-upload', {
              detail: { fileName: file?.name, hash, dir }
            }))
          }
        } catch (e) {
          // Debug: Event dispatch fail (iframe sandbox etc.)
          // console.warn('[OZG] Event ozg-duplicate-upload fehlgeschlagen', e)
        }

        // WICHTIG: Promise-Fehler -> Form.io zeigt die Meldung an der Komponente
        // const err = new Error(msg) // war nur Debug, wird nicht genutzt
        // err.code = 'OZG_DUPLICATE_UPLOAD'
        throw msg
      } else {
        seenUploadContentHashes.add(hash)
      }
    }
    // --------- Ende Duplikat-Erkennung ---------

    const formData = new FormData()
    const originalName = (file && file.name) ? file.name : (fileName || 'upload.bin')
    formData.append('file', file, originalName)

    // WICHTIG: headers kopieren, nicht Original mutieren
    const headers = (options && options.headers) ? { ...options.headers } : {}

    // Context-Token holen und mitsenden
    const token = await getCommunityContextToken()
    headers['X-Community-Context'] = token
    headers['X-OZG-Original-Filename'] = encodeURIComponent(originalName)

    // Optional: CaseId (wenn vorhanden)
    // 1) bevorzugt: URL ?id=...
    const caseIdFromUrl = getParam('id')
    // 2) fallback: X-Form-Identifier wenn du nichts anderes hast
    const caseIdFallback = headers['X-Form-Identifier']

    if (caseIdFromUrl) headers['X-Case-Identifier'] = caseIdFromUrl
    else if (caseIdFallback) headers['X-Case-Identifier'] = caseIdFallback

    const raw = await xhrPost(UPLOAD_URL, formData, headers, progressCallback)

    // Debug: Roh-Response prüfen (key, url, filePath). Wichtig wenn Download/Delete später nicht geht.
    // console.group('[OZG][UPLOAD] server response')
    // console.log('raw:', raw)
    // console.log('raw keys:', raw ? Object.keys(raw) : null)
    // console.log('raw.key:', raw?.key)
    // console.log('raw.filePath:', raw?.filePath)
    // console.log('raw.url:', raw?.url)
    // console.groupEnd()

    const normalizedFilePath = raw?.filePath
      ? String(raw.filePath).replace(/^~\//, '/')
      : null;

    const normalizedUrl = raw?.url
      ? String(raw.url).replace(/^~\//, '/')
      : null;

    const out = {
      storage: 'ozg',
      name: raw?.name || originalName,
      originalName: originalName,
      url: normalizedUrl || (raw?.key ? `${DOWNLOAD_URL}?key=${encodeURIComponent(raw.key)}` : null),
      size: raw?.size ?? file.size,
      type: raw?.type || file.type || 'application/octet-stream',
      key: raw?.key || null,
      filePath: normalizedFilePath,
      data: { key: raw?.key || null, filePath: normalizedFilePath }
    };

    if (typeof progressCallback === 'function') progressCallback(1)

    // Debug: finaler Submission-Block (was Formio in submission speichert)
    // Wenn "Download-Link falsch" oder "Delete geht nicht" -> hier anfangen.
    // console.group('[OZG][UPLOAD] submission object')
    // console.log('out:', out)
    // console.log('out.storage:', out.storage)
    // console.log('out.key:', out.key)
    // console.log('out.filePath:', out.filePath)
    // console.log('out.url:', out.url)
    // console.groupEnd()

    return out
  }

  // Download unverändert
  downloadFile (file) {
    const u = file?.url || (file?.key ? `${DOWNLOAD_URL}?key=${encodeURIComponent(file.key)}` : null)
    return Promise.resolve({ url: u })
  }

  // Delete mit echter Löschlogik
  deleteFile (file) {
    const key = file?.key || file?.data?.key
    if (!key) {
      // Debug: zeigt, wenn submission kein key enthält (dann kann Backend nicht löschen)
      // console.warn('[OZG] DELETE skipped: no key')
      return Promise.resolve()
    }

    // Debug: zeigt, welchen Key du löscht
    // console.log('[OZG] DELETE begin', key)

    return fetch(DELETE_URL, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ Key: key })
    })
      .then(async (res) => {
        const text = await res.text()

        // Debug: Serverantwort beim Delete (IIS 404, 401, JSON-Fehler etc.)
        // console.log('[OZG] DELETE ←', res.status, text.slice(0, 200))

        if (!res.ok) throw new Error(`delete failed ${res.status}: ${text}`)
        return true
      })
      .catch((err) => {
        // Error beim Delete grundsätzlich loggen lassen (sonst Debug unmöglich)
        console.error('[OZG] DELETE error', err)
        throw err
      })
  }
}

// Provider als Factory registrieren (richtige Form für Formio)
Formio.Providers.addProvider('storage', 'ozg', function (options) {
  return new OZGStorageProvider(options || {})
})

export { OZGStorageProvider }
export default OZGStorageProvider
